import { toast } from 'sonner';
import api from '../api';
import { readApiErrorAsync } from '../errors/readApiError';
import { saveBlobAs } from '../saveBlobAs';

/** Shown when the request failed without the server naming a reason (offline, proxy, timeout). */
export const JOB_DOWNLOAD_FALLBACK_KEY = 'job.result.downloadFailed';

/** Server's key for "metadata says there is a file, the store no longer has it" (HTTP 410). */
export const JOB_RESULT_FILE_GONE_KEY = 'job.result.fileNoLongerAvailable';

const FILENAME_PATTERN = /filename\*?=(?:UTF-8'')?"?([^";]+)/i;

export type JobDownloadOutcome =
  | { ok: true }
  /**
   * `fileGone` is the 410 case specifically, kept separate from every other failure because it
   * is the one the caller can act on: the output is not coming back on a retry, so the surface
   * should stop offering the download rather than inviting the user to click again.
   */
  | { ok: false; status?: number; message: string; fileGone: boolean };

interface DownloadJobResultOptions {
  tenantId: string;
  jobId: string;
  /**
   * Name to save under when the response carries no `Content-Disposition`. Receives the response
   * content type so callers can pick an extension; must already be localized by the caller.
   */
  fallbackFileName: (contentType: string) => string;
}

/**
 * Downloads a background job's result file and surfaces any refusal to the user.
 *
 * <b>Why the toast lives here and not at the call sites.</b> All five download surfaces
 * (orders / products / reports deep-links, the job drawer, the import progress card) previously
 * held their own `catch {}` with a comment explaining that the user could just try again. None
 * of them told the user anything, so a 410 was indistinguishable from a completed download that
 * silently produced no file. Reporting inside the helper makes "the user sees the reason" a
 * property of the shared path instead of a rule each new call site has to remember.
 *
 * The outcome is still returned so a caller can react beyond the toast; ignoring the return
 * value is safe and still reports.
 */
export async function downloadJobResult({
  tenantId,
  jobId,
  fallbackFileName,
}: DownloadJobResultOptions): Promise<JobDownloadOutcome> {
  try {
    const r = await api.get(`/api/v1/tenants/${tenantId}/jobs/${jobId}/download`, {
      responseType: 'blob',
    });

    const cd = String(r.headers?.['content-disposition'] ?? '');
    const ct = String(r.headers?.['content-type'] ?? '');
    let fileName = fallbackFileName(ct);
    const m = cd.match(FILENAME_PATTERN);
    if (m && m[1]) fileName = decodeURIComponent(m[1].replace(/"/g, ''));

    saveBlobAs(new Blob([r.data], { type: ct || undefined }), fileName);
    return { ok: true };
  } catch (e) {
    // `readApiErrorAsync`, not `readApiError`: this request set `responseType: 'blob'`, so the
    // refusal body is a Blob and the sync reader would discard the server's reason unread.
    const err = await readApiErrorAsync(e, JOB_DOWNLOAD_FALLBACK_KEY);
    toast.error(err.message);
    return {
      ok: false,
      status: err.status,
      message: err.message,
      fileGone: err.status === 410 || err.messageKey === JOB_RESULT_FILE_GONE_KEY,
    };
  }
}
